import React, { useState, useEffect, useCallback } from 'react';
import './ActiveBabySelector.css';
import { API_BASE_URL } from '../config';

interface Baby {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
  is_owner?: boolean;
  owner_username?: string;
}

interface ActiveBabySelectorProps {
  user: any;
  onBabyChange: (updatedUser: any) => void;
}

const ActiveBabySelector: React.FC<ActiveBabySelectorProps> = ({ user, onBabyChange }) => {
  const [babies, setBabies] = useState<Baby[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchBabies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/babies/user/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setBabies(data);
      } else {
        setError('Failed to load babies');
      }
    } catch (error) {
      console.error('Failed to fetch babies:', error);
      setError('Failed to load babies');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchBabies();
  }, [fetchBabies]);

  const setActiveBaby = async (babyId: string) => {
    setIsUpdating(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}/active-baby?baby_id=${babyId}`, {
        method: 'PUT',
      });

      if (response.ok) {
        // Fetch updated user data
        const userResponse = await fetch(`${API_BASE_URL}/api/users/${user.id}`);
        if (userResponse.ok) {
          const updatedUser = await userResponse.json();
          onBabyChange(updatedUser);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to set active baby');
      }
    } catch (error) {
      setError('Failed to set active baby');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return <div className="active-baby-loading">Loading babies...</div>;
  }

  if (babies.length === 0) {
    return (
      <div className="no-babies">
        <p>No babies available. Create a baby or join with an invite code to get started.</p>
      </div>
    );
  }

  return (
    <div className="active-baby-selector">
      <p className="section-description">Select which baby to track on the main screen:</p>

      {error && <div className="error-message">{error}</div>}

      <div className="baby-options">
        {babies.map((baby) => (
          <div 
            key={baby.id} 
            className={`baby-option ${baby.id === user.active_baby_id ? 'active' : ''}`}
            onClick={() => !isUpdating && setActiveBaby(baby.id)}
          >
            <div className="baby-info">
              <h4>
                {baby.name}
                {baby.is_owner === false && (
                  <span className="shared-indicator">
                    (by {baby.owner_username})
                  </span>
                )}
              </h4>
              <p>Added: {new Date(baby.created_at).toLocaleDateString()}</p>
            </div>
            
            {baby.id === user.active_baby_id && (
              <div className="active-indicator">
                <span className="checkmark">✓</span>
                <span className="active-text">Active</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {isUpdating && (
        <div className="updating-indicator">
          <p>Updating active baby...</p>
        </div>
      )}
    </div>
  );
};

export default ActiveBabySelector;